import type { Image as ImageType } from 'types/capi/shared';

import MailIcon from '~/assets/icons/Mail';
import VectorIcon from '~/assets/icons/Vector';
import { isInAppBrowser } from '~/utils/browser';

import { DialogDismiss } from '../../Dialog';
import Image, { smallImageOptions } from '../../Image';
import Tracked from '../../Tracked';
import type { LoginModalType, ScreenProps } from '.';
import AppleLogin from './ExternalLoginComponents/AppleLogin';
import FacebookLoginButton from './ExternalLoginComponents/Facebook/FacebookLoginButton';
import GoogleLoginButton from './ExternalLoginComponents/GoogleLoginButton';

const modalConfigs: Record<
  LoginModalType,
  { description: string; title: string }
> = {
  notifications: {
    description:
      'Sign in to get an email when these are in stock or drop in price at 100+ retailers.',
    title: 'Get notified on these sneakers'
  },
  purchase: {
    description: '',
    title: 'Sign in to buy these sneakers'
  },
  raffle: {
    description:
      'Sign up or log in to an existing account using one of the methods below.',
    title: 'Sign in to enter this raffle'
  }
};

const ModalHeadings = ({
  modalType,
  image
}: {
  image?: ImageType;
  modalType: LoginModalType;
}) => {
  return (
    <>
      <div className="flex flex-row justify-center">
        <h2 className="mx-auto my-0 text-center font-bold text-black">
          {modalConfigs[modalType].title}
        </h2>
        <DialogDismiss className=" text-black">
          <VectorIcon width="10" height="10" />
        </DialogDismiss>
      </div>

      {image && (
        <div className="my-4">
          <Image
            className="mx-auto aspect-video w-48 object-cover align-middle"
            loading="eager"
            src={image.url}
            alt={image.alt}
            {...smallImageOptions({ pad: true })}
          />
        </div>
      )}

      <h3 className="pb-4 text-center font-normal text-black">
        {modalConfigs[modalType].description}
      </h3>
    </>
  );
};

const InitialScreen = ({
  setScreen,
  onDismiss,
  postAuthOptions,
  setError,
  image,
  modalType
}: ScreenProps) => {
  const isInApp = isInAppBrowser(navigator.userAgent);

  return (
    <>
      <ModalHeadings modalType={modalType} image={image} />

      {!isInApp && (
        <div className="flex items-center justify-center gap-4 pb-4">
          <GoogleLoginButton
            postAuthOptions={postAuthOptions}
            onSuccess={() => onDismiss({ loginSuccessful: true })}
            setError={setError}
          />

          <FacebookLoginButton
            postAuthOptions={postAuthOptions}
            onSuccess={() => onDismiss({ loginSuccessful: true })}
            setError={setError}
          />

          <AppleLogin.Button
            postAuthOptions={postAuthOptions}
            onSuccess={() => onDismiss({ loginSuccessful: true })}
            setError={setError}
          />
        </div>
      )}

      <div className="flex flex-row items-center justify-center gap-4 md:flex-row">
        <Tracked.Button
          className="h-12 w-full px-2"
          variant="forest"
          onClick={() => setScreen('signUp')}
          name="SIGN_UP_EMAIL_PROMPT"
          pagePosition={'Sign in modal'}
        >
          <div className="hidden items-center justify-center gap-2 md:flex">
            <MailIcon height="24" width="24" stroke="white" />
            <p>Sign up with email</p>
          </div>
          <p className="md:hidden">Email sign up</p>
        </Tracked.Button>

        <Tracked.Button
          className="h-12 w-full px-2.5"
          onClick={() => setScreen('signIn')}
          variant="transparent-black-border"
          name="EXISTING_USER_LOGIN_PROMPT"
          pagePosition={'Sign in modal'}
        >
          <div className="hidden items-center justify-center gap-2 md:flex">
            <MailIcon height="24" width="24" stroke="black" />
            <p>Log in with email</p>
          </div>
          <p className="md:hidden">Email log in</p>
        </Tracked.Button>
      </div>
    </>
  );
};

export default InitialScreen;
